import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, LifeBuoy, Mail, Clock, ShieldCheck, Building2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SUPPORT_EMAIL = "support@minemeanddo.com";

export default function Support() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", subject: "General question", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.message) { toast.error("Please add your email and a message"); return; }
    setBusy(true);
    try {
      await api.post("/support", form);
      setSent(true);
      toast.success("Message received — we'll reply within 24–48 hours");
    } catch (err) {
      toast.error("Could not send. Please email us directly.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#f9f9f7] text-[var(--text)]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <button data-testid="support-back" onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-[var(--text-2)] hover:text-[var(--accent)] transition-colors mb-8">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="mb-10">
          <p className="label-mono text-[var(--text-2)] flex items-center gap-2"><LifeBuoy className="w-4 h-4 text-[var(--accent)]" /> Support & Contact</p>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2">How can we <span className="italic text-[var(--accent)]">help</span>?</h1>
          <p className="text-[var(--text-2)] mt-3 max-w-xl">Share a concern, question, or feedback below. Our team replies within <strong>24–48 hours</strong>.</p>
          {user?.platinum_active && (
            <span data-testid="support-priority-badge" className="inline-flex items-center gap-1.5 mt-4 text-xs font-medium bg-[var(--accent)] text-white px-3 py-1 rounded-full">
              <ShieldCheck className="w-3.5 h-3.5" /> Priority support · Business Platinum
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Form */}
          <div className="lg:col-span-2 bg-white border border-[var(--line)] p-6 sm:p-8">
            {sent ? (
              <div data-testid="support-success" className="py-10 text-center">
                <div className="w-14 h-14 rounded-full bg-[var(--accent)]/10 grid place-items-center mx-auto mb-4"><Mail className="w-6 h-6 text-[var(--accent)]" /></div>
                <h2 className="font-serif text-2xl">Message received</h2>
                <p className="text-[var(--text-2)] mt-2">Thanks for reaching out. We'll get back to you at <strong>{form.email}</strong> within 24–48 hours.</p>
                <button data-testid="support-again" onClick={() => { setSent(false); setForm({ name: "", email: "", subject: "General question", message: "" }); }} className="mt-6 h-11 px-5 bg-[#1A1A18] text-white hover:bg-[var(--accent)] transition-colors">Send another</button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <span className="label-mono text-[var(--text-2)]">Name</span>
                    <input data-testid="support-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
                  </div>
                  <div>
                    <span className="label-mono text-[var(--text-2)]">Email *</span>
                    <input data-testid="support-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
                  </div>
                </div>
                <div>
                  <span className="label-mono text-[var(--text-2)]">Topic</span>
                  <select data-testid="support-subject" value={form.subject} onChange={(e) => set("subject", e.target.value)} className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]">
                    {["General question", "Billing & subscription", "Technical issue", "SMS / notifications", "Feature request", "Other"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <span className="label-mono text-[var(--text-2)]">Your concern *</span>
                  <textarea data-testid="support-message" required rows={6} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us what's going on…" className="mt-2 w-full px-3 py-2 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
                </div>
                <button data-testid="support-submit" disabled={busy} className="h-12 px-6 bg-[#1A1A18] text-white hover:bg-[var(--accent)] transition-colors disabled:opacity-50">
                  {busy ? "Sending…" : "Send message"}
                </button>
              </form>
            )}
          </div>

          {/* Info / company */}
          <div className="space-y-4">
            <div className="bg-white border border-[var(--line)] p-6">
              <p className="label-mono text-[var(--text-2)] flex items-center gap-2"><Mail className="w-4 h-4" /> Email us</p>
              <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="support-email-link" className="block mt-2 text-[var(--accent)] font-medium break-all hover:underline">{SUPPORT_EMAIL}</a>
              <p className="text-sm text-[var(--text-2)] mt-3 flex items-center gap-2"><Clock className="w-4 h-4" /> Response within <strong>24–48 hours</strong></p>
            </div>
            <div className="bg-white border border-[var(--line)] p-6">
              <p className="label-mono text-[var(--text-2)] flex items-center gap-2"><Building2 className="w-4 h-4" /> ReMind Me &amp; Do</p>
              <p className="text-sm text-[var(--text-2)] mt-2">Personal reminders and field-service dispatch, all in one place. Serving personal and business users.</p>
              <button data-testid="support-sms-consent" onClick={() => navigate("/sms-consent")} className="mt-3 text-sm text-[var(--accent)] hover:underline flex items-center gap-1"><ShieldCheck className="w-4 h-4" /> SMS terms & consent</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
